import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Plus, Edit2, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function Clientes() {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentCliente, setCurrentCliente] = useState(null);
  const [filtroGlobal, setFiltroGlobal] = useState('');
  const [menuAcoesAberto, setMenuAcoesAberto] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clienteParaExcluir, setClienteParaExcluir] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    cidade: '',
    tipo_cliente: 'Voucher',
    numero_associado: '',
    vinculo: 'Titular',
  });

  useEffect(() => {
    carregarClientes();
  }, []);

  const carregarClientes = async () => {
    try {
      const res = await axios.get(`${API}/clientes`);
      setClientes(res.data);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await axios.put(`${API}/clientes/${currentCliente.id}`, formData);
        toast.success('Cliente atualizado com sucesso!');
      } else {
        await axios.post(`${API}/clientes`, formData);
        toast.success('Cliente criado com sucesso!');
      }
      setDialogOpen(false);
      resetForm();
      carregarClientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar cliente');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      telefone: '',
      cidade: '',
      tipo_cliente: 'Voucher',
      numero_associado: '',
      vinculo: 'Titular',
    });
    setEditMode(false);
    setCurrentCliente(null);
  };

  const handleEdit = (cliente) => {
    setCurrentCliente(cliente);
    setFormData({
      nome: cliente.nome,
      telefone: cliente.telefone,
      cidade: cliente.cidade,
      tipo_cliente: cliente.tipo_cliente,
      numero_associado: cliente.numero_associado || '',
      vinculo: cliente.vinculo || 'Titular',
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDeleteClick = (cliente) => {
    setClienteParaExcluir(cliente);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!clienteParaExcluir) return;
    try {
      await axios.delete(`${API}/clientes/${clienteParaExcluir.id}`);
      toast.success('Cliente excluído com sucesso!');
      carregarClientes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir cliente');
    } finally {
      setDeleteDialogOpen(false);
      setClienteParaExcluir(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="clientes-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 lg:mb-8 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-1 lg:mb-2">Clientes</h1>
          <p className="text-sm sm:text-base text-slate-600">Gerencie o cadastro de clientes</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700 w-full sm:w-auto" data-testid="criar-cliente-btn">
              <Plus className="mr-2 h-4 w-4 sm:h-5 sm:w-5" /> Novo Cliente
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editMode ? 'Editar Cliente' : 'Novo Cliente'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Nome Completo *</Label>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Nome do cliente"
                  required
                  data-testid="input-nome"
                />
              </div>

              <div>
                <Label>Telefone *</Label>
                <Input
                  value={formData.telefone}
                  onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                  placeholder="(11) 99999-9999"
                  required
                  data-testid="input-telefone"
                />
              </div>

              <div>
                <Label>Cidade *</Label>
                <Input
                  value={formData.cidade}
                  onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                  placeholder="Cidade"
                  required
                  data-testid="input-cidade"
                />
              </div>

              <div>
                <Label>Tipo de Cliente *</Label>
                <Select
                  value={formData.tipo_cliente}
                  onValueChange={(value) => setFormData({ ...formData, tipo_cliente: value })}
                >
                  <SelectTrigger data-testid="select-tipo-cliente">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Associado">Associado</SelectItem>
                    <SelectItem value="Voucher">Voucher</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Vínculo *</Label>
                <Select
                  value={formData.vinculo}
                  onValueChange={(value) => setFormData({ ...formData, vinculo: value })}
                >
                  <SelectTrigger data-testid="select-vinculo">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Titular">Titular</SelectItem>
                    <SelectItem value="Dependente">Dependente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.tipo_cliente === 'Associado' && (
                <div>
                  <Label>Número do Associado</Label>
                  <Input
                    value={formData.numero_associado}
                    onChange={(e) => setFormData({ ...formData, numero_associado: e.target.value })}
                    placeholder="Número do associado"
                    data-testid="input-numero-associado"
                  />
                </div>
              )}

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="salvar-cliente-btn">
                  {editMode ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Campo de busca */}
      <div className="mb-4">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Buscar clientes..."
            value={filtroGlobal}
            onChange={(e) => setFiltroGlobal(e.target.value)}
            className="pl-10 text-sm"
          />
        </div>
      </div>

      {/* Layout Mobile - Cards */}
      <div className="lg:hidden space-y-4">
        {clientes.filter(c => 
          c.nome?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
          c.telefone?.includes(filtroGlobal) ||
          c.cidade?.toLowerCase().includes(filtroGlobal.toLowerCase())
        ).length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-8 text-center text-slate-500">
            {filtroGlobal ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado'}
          </div>
        ) : (
          clientes.filter(c => 
            c.nome?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
            c.telefone?.includes(filtroGlobal) ||
            c.cidade?.toLowerCase().includes(filtroGlobal.toLowerCase())
          ).map((cliente) => (
            <div key={cliente.id} className="bg-white rounded-lg shadow-sm border border-slate-200 p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-800 text-sm">{cliente.nome}</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{cliente.telefone}</p>
                </div>
                <Badge variant={cliente.tipo_cliente === 'Associado' ? 'default' : 'secondary'} className="text-xs">
                  {cliente.tipo_cliente}
                </Badge>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Cidade:</span>
                  <span className="font-medium text-slate-800">{cliente.cidade}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Vínculo:</span>
                  <Badge className={cliente.vinculo === 'Titular' ? 'bg-blue-100 text-blue-800 text-xs' : 'bg-purple-100 text-purple-800 text-xs'}>
                    {cliente.vinculo || 'Titular'}
                  </Badge>
                </div>
                {cliente.numero_associado && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Nº Associado:</span>
                    <span className="font-medium text-slate-800">{cliente.numero_associado}</span>
                  </div>
                )}
              </div>
              <div className="mt-4 pt-3 border-t border-slate-200">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(cliente)}
                  className="w-full"
                >
                  <Edit2 className="h-4 w-4 mr-2" />
                  Editar
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Layout Desktop - Tabela */}
      <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs xl:text-sm">Nome</TableHead>
              <TableHead className="text-xs xl:text-sm">Telefone</TableHead>
              <TableHead className="text-xs xl:text-sm hidden xl:table-cell">Cidade</TableHead>
              <TableHead className="text-xs xl:text-sm">Tipo</TableHead>
              <TableHead className="text-xs xl:text-sm hidden xl:table-cell">Vínculo</TableHead>
              <TableHead className="text-xs xl:text-sm hidden 2xl:table-cell">Nº Associado</TableHead>
              <TableHead className="text-xs xl:text-sm w-16"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {clientes.filter(c => 
              c.nome?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
              c.telefone?.includes(filtroGlobal) ||
              c.cidade?.toLowerCase().includes(filtroGlobal.toLowerCase())
            ).length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                  {filtroGlobal ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado'}
                </TableCell>
              </TableRow>
            ) : (
              clientes.filter(c => 
                c.nome?.toLowerCase().includes(filtroGlobal.toLowerCase()) ||
                c.telefone?.includes(filtroGlobal) ||
                c.cidade?.toLowerCase().includes(filtroGlobal.toLowerCase())
              ).map((cliente) => (
                <TableRow key={cliente.id} data-testid={`cliente-row-${cliente.id}`}>
                  <TableCell className="font-medium text-xs xl:text-sm">{cliente.nome}</TableCell>
                  <TableCell className="text-xs xl:text-sm">{cliente.telefone}</TableCell>
                  <TableCell className="text-xs xl:text-sm hidden xl:table-cell">{cliente.cidade}</TableCell>
                  <TableCell>
                    <Badge variant={cliente.tipo_cliente === 'Associado' ? 'default' : 'secondary'} className="text-xs">
                      {cliente.tipo_cliente}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden xl:table-cell">
                    <Badge className={`text-xs ${cliente.vinculo === 'Titular' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>
                      {cliente.vinculo || 'Titular'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs xl:text-sm hidden 2xl:table-cell">{cliente.numero_associado || '-'}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(cliente)}
                        className="h-8 w-8 p-0"
                        data-testid={`edit-cliente-${cliente.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteClick(cliente)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-cliente-${cliente.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar Exclusão</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Tem certeza que deseja excluir o cliente <strong>{clienteParaExcluir?.nome}</strong>?
            </p>
            <p className="text-sm text-red-600 mt-2">Esta ação não pode ser desfeita.</p>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">
              Excluir
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}